const AnalyticsGoogleStatic = {
    isLibraryLoaded: false
};

/**
 * Tracker class, needs to implement the following interface:
 * class AnalyticsTracker{
 *      constructor(settings){};
 *      trackPageView(details={}){};
 *      trackEvent(details={}){};
 *      remove(){};
 * }
 * After the tracker is created it can be added to the Analytics class. You can trigger all events through
 * the Analytics class too. That way all trackers will be targetted.
 */
class AnalyticsGoogle {
    static gtag(){
        // Google Analytics needs to have this as an Argument instance, a normal array doesn't work.
        /* eslint-disable-next-line */
        window['dataLayer'].push(arguments);
    };

    /**
     * Loading the Google Analytics library
     */
    static loadLibrary(trackerId){
        AnalyticsGoogleStatic.isLibraryLoaded = true;
        // Set initial gt.ag/js?id=<first ID> script to <head>
        // NOTE: gtag script only needs to be set to page once, but each gtag('config', <ID>) that's pushed to
        // the dataLayer will add subsequent gtag/js?id=<ID> scripts to the page
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + trackerId;
        document.getElementsByTagName('head')[0].appendChild(script);
        window['dataLayer'] = window['dataLayer'] || [];
        AnalyticsGoogle.gtag('js', new Date());
    }

    /**
     * Loading Google Tag Manager, which will in turn load Google Analytics if configured to do so. Typically
     * you would configure at least one Google Analytics tag in GTM to be triggered on page load.
     * @param {string} gtmId The GTM ID, which has a format like: GTM-XXXXXX
     */
    static loadGTM(gtmId){
        window['dataLayer'] = window['dataLayer'] || [];
        window['dataLayer'].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
            // Disable automatic page view tracking
            'gtm.uniqueEventId': 0
        });
        const existingScript = document.getElementsByTagName('script')[0];
        const newScript = document.createElement('script');
        newScript.async = true;
        newScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId;
        existingScript.parentNode.insertBefore(newScript, existingScript);
    }

    constructor(settings){
        this.trackerId = settings.trackerId;
        this.type = settings.type || 'GA';
        this.isCustomPageViewEnabled = false; // send page views as custom events
        if(! AnalyticsGoogleStatic.isLibraryLoaded){
            if(settings.type === 'GTM'){
                AnalyticsGoogle.loadGTM(this.trackerId);
            }else{
                AnalyticsGoogle.loadLibrary(this.trackerId);
            }
        }
        this.init(this.trackerId);
    }

    // Initializing this tracker
    init(trackerId){
        if(this.type === 'GA'){
            // if the tracker was disabled before, we need to re-enable it
            delete window['ga-disable-' + trackerId];
            // disable automatic page view tracking as we do it manually. This cannot be done when using GTM,
            // then this setting needs to be configured properly in the GTM interface.
            AnalyticsGoogle.gtag('config', trackerId, {
                send_page_view: false
            });
        }
    }

    // Enable or disable sending page views as custom events.
    enableCustomPageView(isEnable = true){
        this.isCustomPageViewEnabled = isEnable;
    }

    // sending a page view. Settings can contain path, location and title. If location is set, path is
    // ignored. If title isn't set, the current document title is used.
    trackPageView(settings = {}){
        const data = {
            page_title: settings.title,
            page_location: settings.location
        };
        // For GTM we rely on the automatic page path detection, so we don't trigger it here.
        if(this.type === 'GA'){
            data.send_to = this.trackerId;
            AnalyticsGoogle.gtag('event', 'page_view', data);
        }
        // When enabled we can send out a custom event with the details of the new page view
        if(this.isCustomPageViewEnabled){
            this.trackEvent({
                action: 'custom_page_view',
                category: settings.location,
                label: settings.title
            });
        }
    }

    // sending an event. Google analytics supports category, action and label as values in settings
    trackEvent(settings){
        const data = {
            event_category: settings.category,
            event_label: settings.label,
            value: settings.value
        };
        if(this.type === 'GA'){
            data.send_to = this.trackerId;
            AnalyticsGoogle.gtag('event', settings.action, data);
        }else{
            const eventName = 'custom_' + settings.action;
            window.eventCategory = settings.category;
            // push it directly to dataLayer, so it's inserted as an object rather than an array, this allows
            // GTM to read the dataLayer values properly (otherwise it doesn't seem to find them)
            data.event = eventName;
            window['dataLayer'].push(data);
        }
    }

    remove(){
        // This should stop the tracker from sending any data.
        if(this.type === 'GA'){
            window['ga-disable-' + this.trackerId] = true;
        }
    }
}

export {AnalyticsGoogle};
